import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

class ProductAccessoriesTotal extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.closest('form');
    if (!this.form) return;
    const data = JSON.parse(this.querySelector('[data-price-data]').textContent);
    this.machinePrices = new Map(data.machine.map((variant) => [String(variant.id), variant.price]));
    this.machineAvailability = new Map(data.machine.map((variant) => [String(variant.id), variant.available]));
    this.accessoryPrices = new Map(data.accessories.flatMap((variants) => variants || [])
      .map((variant) => [String(variant.id), variant.price]));
    this.form.addEventListener('change', () => this.update(), { signal });
    this.form.addEventListener('input', () => this.update(), { signal });
    this.form.addEventListener('accessories:reset', () => this.update(), { signal });
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      if (String(event.detail.data.productId) !== this.form.closest('product-form-component')?.dataset.productId) return;
      // Horizon updates the form's variant ID in its own listener first.
      queueMicrotask(() => this.update());
    }, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.update(), { signal });
    window.addEventListener('pageshow', () => this.update(), { signal });
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  money(cents) {
    // Liquid/Ajax prices use hundredths even for zero-decimal currencies;
    // the theme formatter expects the currency's native minor units.
    const currency = this.dataset.currency;
    const decimals = new Intl.NumberFormat('en', { style: 'currency', currency })
      .resolvedOptions().maximumFractionDigits;
    return formatMoney(cents * (10 ** decimals) / 100, this.dataset.moneyFormat, currency);
  }

  update() {
    const variantId = this.form.querySelector('[name="id"]')?.value;
    const machinePrice = this.machinePrices.get(variantId);
    const quantity = Math.max(1, Number.parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10));
    let accessoriesPrice = 0;
    let valid = Number.isFinite(machinePrice) && Number.isFinite(quantity);
    const selectedIds = new Set();
    this.form.querySelectorAll('.addon-checkbox').forEach((checkbox) => {
      const id = checkbox.dataset.variantId;
      const price = this.accessoryPrices.get(id);
      if (Number.isFinite(price)) {
        const label = checkbox.closest('label')?.querySelector('.addon-right');
        if (label) label.textContent = `+${this.money(price)}`;
      }
      // Match the cart handler: one of each accessory, deduplicated by variant.
      if (!checkbox.checked || !Number(id) || id === variantId || selectedIds.has(id)) return;
      selectedIds.add(id);
      if (!Number.isFinite(price)) valid = false;
      else accessoriesPrice += price;
    });
    const button = this.form.querySelector('button[name="add"]');
    const content = button?.querySelector('.add-to-cart-text__content');
    if (!content) return;
    let amount = content.querySelector('[data-selection-total]');
    if (!amount) {
      amount = document.createElement('span');
      amount.className = 'add-to-cart-selection-total';
      amount.dataset.selectionTotal = '';
      amount.setAttribute('aria-live', 'polite');
      amount.setAttribute('aria-atomic', 'true');
      content.append(amount);
    }
    const variantAvailable = this.machineAvailability.get(variantId);
    amount.hidden = !valid || !variantAvailable;
    if (!valid || !variantAvailable) return;
    const text = ` · ${this.money(machinePrice * quantity + accessoriesPrice)}`;
    // Avoid repeating live announcements when unrelated form inputs change.
    if (amount.textContent !== text) amount.textContent = text;
  }
}

if (!customElements.get('product-accessories-total')) {
  customElements.define('product-accessories-total', ProductAccessoriesTotal);
}
